import React from 'react';
import type { RegistrationQuestion, RegistrationAnswers } from '@/services/creatorApi';

// Renders a creator's registration questions (same types as casting's custom
// questions builder, minus uploads) as a controlled form. The parent owns the
// answers object; this only draws inputs and reports changes. `tone` picks the
// palette: 'dark' for the event page, 'cream' for builder previews.
export function RegistrationQuestionsForm({ questions, answers, onChange, tone = 'dark' }: {
  questions: RegistrationQuestion[];
  answers: RegistrationAnswers;
  onChange: (next: RegistrationAnswers) => void;
  tone?: 'dark' | 'cream';
}) {
  const ink = tone === 'dark' ? 'var(--hj-ink)' : 'var(--hj-beige-ink)';
  const line = tone === 'dark' ? 'var(--hj-hairline)' : 'var(--hj-beige-line)';
  const field = tone === 'dark' ? 'var(--hj-surface-2)' : '#fff';
  const set = (id: string, v: string | string[]) => onChange({ ...answers, [id]: v });
  const inputStyle: React.CSSProperties = { width: '100%', padding: '9px 11px', borderRadius: 'var(--hj-r-sm)', border: `1px solid ${line}`, background: field, color: ink, fontSize: 14, fontFamily: 'inherit' };
  const optionRow: React.CSSProperties = { display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 10px', borderRadius: 'var(--hj-r-sm)', border: `1px solid ${line}`, background: field, cursor: 'pointer', color: ink, fontSize: 14, lineHeight: 1.4 };
  return (
    <div className="flex flex-col gap-4">
      {questions.map(q => {
        const label = <span style={{ display: 'block', fontSize: 14, fontWeight: 600, color: ink, marginBottom: 8 }}>{q.label}{q.required ? <span aria-hidden="true" style={{ marginLeft: 4 }}>*</span> : null}</span>;
        if (q.type === 'text') return <label key={q.id}>{label}<input type="text" value={(answers[q.id] as string) || ''} onChange={e => set(q.id, e.target.value)} style={inputStyle} maxLength={300} /></label>;
        if (q.type === 'textarea') return <label key={q.id}>{label}<textarea value={(answers[q.id] as string) || ''} onChange={e => set(q.id, e.target.value)} rows={3} style={{ ...inputStyle, resize: 'vertical' }} maxLength={2000} /></label>;
        if (q.type === 'select') return (
          <label key={q.id}>{label}
            <select value={(answers[q.id] as string) || ''} onChange={e => set(q.id, e.target.value)} style={inputStyle}>
              <option value="">Choose one</option>
              {(q.options || []).map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </label>
        );
        if (q.type === 'yesno') return (
          <fieldset key={q.id} className="m-0 p-0" style={{ border: 0 }}>
            <legend className="p-0">{label}</legend>
            <div className="flex gap-2">
              {['yes', 'no'].map(v => (
                <label key={v} style={{ ...optionRow, flex: 1, justifyContent: 'center', background: answers[q.id] === v ? 'var(--hj-yellow)' : field, color: answers[q.id] === v ? 'oklch(0.18 0.005 70)' : ink, fontWeight: 600 }}>
                  <input type="radio" name={q.id} value={v} checked={answers[q.id] === v} onChange={() => set(q.id, v)} style={{ display: 'none' }} />
                  {v === 'yes' ? 'Yes' : 'No'}
                </label>
              ))}
            </div>
          </fieldset>
        );
        if (q.type === 'radio') return (
          <fieldset key={q.id} className="m-0 p-0" style={{ border: 0 }}>
            <legend className="p-0">{label}</legend>
            <div className="flex flex-col gap-1.5">
              {(q.options || []).map(o => (
                <label key={o} style={optionRow}>
                  <input type="radio" name={q.id} value={o} checked={answers[q.id] === o} onChange={() => set(q.id, o)} style={{ marginTop: 3, accentColor: 'var(--hj-yellow)' }} />
                  <span>{o}</span>
                </label>
              ))}
            </div>
          </fieldset>
        );
        // checkbox (multiple choice)
        const picked = Array.isArray(answers[q.id]) ? (answers[q.id] as string[]) : [];
        return (
          <fieldset key={q.id} className="m-0 p-0" style={{ border: 0 }}>
            <legend className="p-0">{label}</legend>
            <div className="flex flex-col gap-1.5">
              {(q.options || []).map(o => (
                <label key={o} style={optionRow}>
                  <input type="checkbox" checked={picked.includes(o)} onChange={e => set(q.id, e.target.checked ? [...picked, o] : picked.filter(x => x !== o))} style={{ marginTop: 3, accentColor: 'var(--hj-yellow)' }} />
                  <span>{o}</span>
                </label>
              ))}
            </div>
          </fieldset>
        );
      })}
    </div>
  );
}

// True when every required question has an answer.
export function answersComplete(questions: RegistrationQuestion[], answers: RegistrationAnswers): boolean {
  return questions.every(q => {
    if (!q.required) return true;
    const v = answers[q.id];
    return Array.isArray(v) ? v.length > 0 : !!(v && String(v).trim());
  });
}

// "Label: answer" lines for rosters and exports.
export function answersToLines(questions: RegistrationQuestion[], answers?: RegistrationAnswers | null): { label: string; value: string }[] {
  if (!answers) return [];
  return questions.map(q => {
    const v = answers[q.id];
    const value = Array.isArray(v) ? v.join('; ') : (v == null ? '' : String(v));
    return { label: q.label, value };
  }).filter(x => x.value);
}
